import reactUpdate, {Spec} from 'immutability-helper';

import {assert} from 'Asserts';
import Editor from 'Editor';
import {getNoteLinkedGroupAdapters} from 'notebook/notes/groups/NoteLinkedGroupAdapters';
import {NoteLinkedGroup, UnknownNoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {reduceGroupUpdateNotePart} from 'notebook/notes/groups/util/ContainedNotePartUtil';
import {getTablePlugin} from 'notebook/notes/plugins/getTablePlugin';
import {FullTableMap} from 'notebook/tables/excess/Evaluator';
import {
  getTableFromNotePart,
  getTableNameFromNotePart,
  getTableNotePartLocations,
} from 'notebook/tables/store/selectors/TableSelectors';
import {Table} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';
import {maybeWithLoadingScreen} from 'util/Promises';

export function getNotePartSpecFromTable(table: Table, notePart: NotePart): Spec<NotePart> {
  const plugin = getTablePlugin(notePart);
  // Neither the table or structured table plugins use this editor instance, so it does not need to be real.
  return plugin.setContent.call({} as Editor, {
    table,
  }) as Spec<NotePart>;
}

export function updateNoteWithTableMap(tableMap: FullTableMap, note: Note): Note {
  const topLevelSpecs: {[i: number]: Spec<NotePart>} = {};
  // Accumulate updated groups per top-level index so multiple tables inside the same group merge correctly.
  const updatedGroupsByTopIndex = new Map<number, NoteLinkedGroup<unknown>>();

  for (const location of getTableNotePartLocations(note)) {
    const oldTable = getTableFromNotePart(location.notePart).table;
    const newTable = tableMap[oldTable.name];
    if (newTable == null || oldTable === newTable) {
      continue;
    }
    if (TableUtil.isInvalidOpWithWarn(oldTable, newTable, {})) {
      return note;
    }
    const updatedNotePart = reactUpdate(
      location.notePart,
      getNotePartSpecFromTable(newTable, location.notePart)
    );
    if (location.containedIndex == null) {
      topLevelSpecs[location.topIndex] = {$set: updatedNotePart};
    } else {
      const currentGroup =
        updatedGroupsByTopIndex.get(location.topIndex) ??
        (note.noteParts[location.topIndex].item as NoteLinkedGroup<unknown>);
      updatedGroupsByTopIndex.set(
        location.topIndex,
        reduceGroupUpdateNotePart(currentGroup, updatedNotePart)
      );
    }
  }

  const noteParts: {[i: number]: Spec<NotePart>} = {...topLevelSpecs};
  for (const [topIndex, group] of updatedGroupsByTopIndex) {
    noteParts[topIndex] = {item: {$set: group}};
  }
  return reactUpdate(note, {noteParts});
}

/**
 * Updates a single table within a note, supporting tables nested inside note linked groups.
 *
 * For tables nested in a note linked group, this routes through the group adapter's `reduceGroup`,
 * which may be asynchronous (e.g. mixture prep amount / parent-mixture propagation). The async
 * branch is wrapped in a loading screen to match the in-editor update UX.
 */
export async function updateTableInNote(note: Note, table: Table): Promise<Note> {
  const location = getTableNotePartLocations(note).find(
    (loc) => getTableNameFromNotePart(loc.notePart) === table.name
  );
  assert(location != null, `Note part not found for ${table.name}`);
  const updatedNotePart = reactUpdate(location.notePart, getNotePartSpecFromTable(table, location.notePart));

  if (location.containedIndex == null) {
    return reactUpdate(note, {noteParts: {[location.topIndex]: {$set: updatedNotePart}}});
  }

  const group = note.noteParts[location.topIndex].item as UnknownNoteLinkedGroup;
  const newGroup = await maybeWithLoadingScreen(
    getNoteLinkedGroupAdapters()[group.kind].reduceGroup(group, updatedNotePart),
    'Updating...'
  );
  return reactUpdate(note, {noteParts: {[location.topIndex]: {item: {$set: newGroup}}}});
}
